import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { API_URL } from "../utils/api";

const AuthContext = createContext(null);

// Helper to create a clean, serializable user object
const createCleanUserObject = (userData) => {
  if (!userData) return null;
  
  // Extract only primitive values to ensure serializability
  return {
    user_id: String(userData.user_id || ""),
    email: String(userData.email || ""),
    name: String(userData.name || ""),
    role: String(userData.role || "sales"),
    permissions: {
      can_manage_users: Boolean(userData.permissions?.can_manage_users),
      can_view_all_data: Boolean(userData.permissions?.can_view_all_data),
      can_edit_all_data: Boolean(userData.permissions?.can_edit_all_data),
      can_manage_settings: Boolean(userData.permissions?.can_manage_settings)
    }
  };
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      // Use httpOnly cookie authentication - no localStorage token needed
      // The cookie is automatically sent with credentials: 'include'
      let response;
      try {
        response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include' // Sends httpOnly cookies automatically
        });
      } catch (fetchError) {
        console.error("Fetch error in checkAuth:", fetchError);
        setUser(null);
        return;
      }

      if (response.ok) {
        try {
          const responseText = await response.text();
          const data = JSON.parse(responseText);
          const cleanUser = createCleanUserObject(data.user);
          setUser(cleanUser);
        } catch (parseError) {
          console.error("JSON parse error in checkAuth:", parseError);
          setUser(null);
        }
      } else {
        // Not authenticated or token expired
        setUser(null);
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setUser(null);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData) => {
    try {
      // Token is now stored in httpOnly cookie by backend
      // We only need to store user data in state
      const cleanUserData = createCleanUserObject(userData);
      setUser(cleanUserData);
    } catch (error) {
      console.error("AuthContext login error:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: "POST",
        credentials: 'include' // Include httpOnly cookie
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
    }
  };

  // For API calls that need authentication
  const fetchWithAuth = async (url, options = {}) => {
    return fetch(url, {
      ...options,
      credentials: 'include' // Always include httpOnly cookies
    });
  };

  // Helper to get auth headers (for compatibility) - Note: with httpOnly cookies,
  // credentials: 'include' is what matters, not headers
  const getAuthHeaders = () => {
    return {
      "Content-Type": "application/json"
    };
  };

  const hasPermission = (permission) => {
    if (!user?.permissions) return false;
    return user.permissions[permission] === true;
  };

  const isAdmin = () => user?.role === "admin";
  const isSales = () => user?.role === "sales";

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      initialized,
      login,
      logout,
      checkAuth,
      fetchWithAuth,
      getAuthHeaders,
      hasPermission,
      isAdmin,
      isSales,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
